import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { queueCampaignTest } from "../../../helpers/sends_helper";
import type { Campaign } from "../../../helpers/campaigns_helper";
import {
  checkCampaignDeliverability,
  type DeliverabilityCheckResult,
} from "../../../helpers/deliverability_helper";
import PreflightPanel from "./PreflightPanel";

type Props = {
  campaign: Campaign | null;
  isOpen: boolean;
  onClose: () => void;
};

type Phase = "form" | "preflight";

/**
 * Modal de e-mail de teste (1/min, sem cota) — DOC 03 § 6.2 · 13 UX.
 * Após enfileirar: mostra Pré-voo antes de fechar (F2).
 */
const TestCampaignModal = ({ campaign, isOpen, onClose }: Props) => {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<Phase>("form");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [preflightLoading, setPreflightLoading] = useState(false);
  const [preflightResult, setPreflightResult] =
    useState<DeliverabilityCheckResult | null>(null);
  const [preflightError, setPreflightError] = useState<string | null>(null);

  const runPreflight = useCallback(async () => {
    if (!campaign?.id) {
      return;
    }
    setPreflightLoading(true);
    setPreflightError(null);
    try {
      const res = await checkCampaignDeliverability(campaign.id, {
        subject: campaign.subject,
        html_body: campaign.html_body,
      });
      if (res?.success && res.data) {
        setPreflightResult(res.data);
      } else {
        setPreflightResult(null);
        setPreflightError(t("preflight.retry"));
      }
    } catch (e: any) {
      setPreflightResult(null);
      setPreflightError(e?.message || t("preflight.retry"));
    } finally {
      setPreflightLoading(false);
    }
  }, [campaign, t]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setPhase("form");
    setEmail("");
    setEmailError(null);
    setFormError(null);
    setPreflightResult(null);
    setPreflightError(null);
    setPreflightLoading(false);
  }, [isOpen]);

  const submit = async () => {
    if (!campaign) {
      return;
    }
    setFormError(null);
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setEmailError(t("sending.modals.test.emailInvalid"));
      return;
    }
    setEmailError(null);
    setBusy(true);
    try {
      const res = await queueCampaignTest(campaign.id, { email: trimmed });
      toast.success(res?.message || t("sending.toast.testQueued"), {
        autoClose: 3000,
      });
      setPhase("preflight");
      void runPreflight();
    } catch (e: any) {
      const code = e?.code as string | undefined;
      if (code === "TEST_SEND_RATE_LIMITED") {
        toast.warning(e?.message || t("sending.toast.testRateLimited"), {
          autoClose: 5000,
        });
      } else if (code === "SETUP_INCOMPLETE") {
        toast.error(
          <span>
            {e?.message || t("sending.error.generic")}{" "}
            <Link
              to="/settings/sending"
              className="text-decoration-underline fw-semibold"
            >
              {t("sending.toast.openSetup")}
            </Link>
          </span>,
          { autoClose: 8000 }
        );
      } else if (code === "SUBSCRIPTION_INACTIVE") {
        toast.error(e?.message || t("sending.error.generic"), {
          autoClose: 5000,
        });
      } else {
        setFormError(e?.message || t("sending.error.test"));
      }
    } finally {
      setBusy(false);
    }
  };

  const handleClose = () => {
    if (busy) {
      return;
    }
    onClose();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered size={phase === "preflight" ? "lg" : undefined}>
      <ModalHeader toggle={handleClose}>
        {phase === "preflight"
          ? t("preflight.afterTestTitle")
          : t("sending.modals.test.title")}
      </ModalHeader>
      <ModalBody>
        {phase === "form" ? (
          <>
            <p className="text-muted mb-3">
              {t("sending.modals.test.help", { name: campaign?.name })}
            </p>
            {formError && (
              <Alert color="danger" className="mb-3">
                {formError}
              </Alert>
            )}
            <div>
              <Label className="form-label" htmlFor="test-send-email">
                {t("sending.modals.test.emailLabel")}
              </Label>
              <Input
                id="test-send-email"
                type="email"
                value={email}
                invalid={!!emailError}
                disabled={busy}
                placeholder={t("sending.modals.test.emailPlaceholder")}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void submit();
                  }
                }}
              />
              {emailError && <FormFeedback>{emailError}</FormFeedback>}
              <p className="text-muted small mt-2 mb-0">
                {t("sending.modals.test.rateHint")}
              </p>
            </div>
          </>
        ) : (
          <>
            <Alert color="success" className="mb-3 py-2">
              {t("sending.toast.testQueued")}
            </Alert>
            <PreflightPanel
              result={preflightResult}
              loading={preflightLoading}
              error={preflightError}
              onRecheck={() => void runPreflight()}
            />
          </>
        )}
      </ModalBody>
      <ModalFooter>
        {phase === "form" ? (
          <>
            <Button color="light" disabled={busy} onClick={handleClose}>
              {t("sending.modals.cancel")}
            </Button>
            <Button color="primary" disabled={busy} onClick={() => void submit()}>
              {busy ? <Spinner size="sm" /> : t("sending.modals.test.submit")}
            </Button>
          </>
        ) : (
          <Button color="primary" onClick={handleClose}>
            {t("sending.modals.cancel")}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default TestCampaignModal;
